import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../data/cloud-data.js';
import { Configuration, HardTopOptions } from '../../data/configuration.js';
import { ExteriorConfigurator } from '../../configurator/exterior-configurator.js';
import { VehicleView } from '../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../view-content.js';
import SelectedSectionItem from '../ui/selected-section-item.tsx';
import { AppContext } from '../app.tsx';
import ViewContentRows from '../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface ExteriorTextilesViewProps {
	exteriorConfigurator: ExteriorConfigurator;
	onBackButtonClicked: () => void;
}

interface ExteriorTextilesViewContextType {
	selectedHoodItemId?: string;
	handleHoodItemClicked?: (id:string) => void;
	selectedHardTopItemId: string;
	handleHardTopItemClicked: (id:string) => void;
	toggleHood: () => void;
	hoodUp: boolean;
	toggleHardTop: () => void;
	hardTopVisible: boolean;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function ExteriorTextilesView(props: ExteriorTextilesViewProps) {
	if (Configuration.isSupersport400Model()) {
		return ExteriorTextilesView400(props);
	}
	else {
		return ExteriorTextilesViewStandard(props);
	}
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

function ExteriorTextilesView400(props: ExteriorTextilesViewProps) {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// initial values.
	const initialHardTopItemId = Configuration.hardTopOption;
	const initialHardTopItemName = () => getHardTopItemName(initialHardTopItemId);

	// states.
	const [selectedHardTopItemId, setSelectedHardTopItemId] = useState<string>(initialHardTopItemId);
	const [selectedHardTopItemName, setSelectedHardTopItemName] = useState<string>(initialHardTopItemName);

	// handlers.

	const handleHardTopItemClicked = (id: string) => {

		// if the item is already selected, toggle it to "None" (no hard top).
		if (id === selectedHardTopItemId) {
			id = HardTopOptions.None;
		}

		// update the selected item and item name.
		setSelectedHardTopItemId(id);
		let hardTopName = getHardTopItemName(id);

		if (id === HardTopOptions.None) {
			hardTopName = '';
		}

		setSelectedHardTopItemName(hardTopName);

		// update the configuration.
		Configuration.hardTopOption = id as HardTopOptions;
		Configuration.updateCosts();

		// select the hard top style on the car.
		props.exteriorConfigurator.selectHardTop(id, context.hoodUp);

		// also set visibility of hard top icon in bottom left UI.
		context.setHardTopVisible(id !== HardTopOptions.None);
	};

	// return the HTML.
	return (
		<ExteriorTextilesViewContext.Provider value={{ selectedHardTopItemId, handleHardTopItemClicked, toggleHood: context.toggleHood, hoodUp: context.hoodUp, hardTopVisible: context.hardTopVisible, toggleHardTop: context.toggleHardTop }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-exterior-textiles">
							ROOF
						</h2>
					</div>

					<SectionPanel
						subHeading={'HARD TOP'}
						modalContent={CloudData.getModalContent(selectedHardTopItemId, CloudData.hardTopOptions)}
					>
						<SelectedSectionItem
							itemName={selectedHardTopItemName}
							sectionPrice={CloudData.getPrice(selectedHardTopItemId, CloudData.hardTopOptions, { ref: false })}
						/>
						<HardTopItems400 />
					</SectionPanel>

					{selectedHardTopItemId !== HardTopOptions.BodyColoured && selectedHardTopItemId !== HardTopOptions.GreyHardTopS400 && (
						<SectionPanel
							subHeading={'HOOD VIEW'}>
							<SelectedSectionItem
								itemName={'Click to view hood up/down'} />
							<HoodViewItems />
						</SectionPanel>
					)}

				</div>
			</VehicleView>
		</ExteriorTextilesViewContext.Provider>
	);
}

function ExteriorTextilesViewStandard(props: ExteriorTextilesViewProps) {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// initial values.
	const initialHoodItemId = Configuration.hoodStyleOption;
	const initialHoodItemName = () => getItemName(initialHoodItemId);
	const initialHardTopItemId = Configuration.hardTopOption;
	const initialHardTopItemName = () => getHardTopItemName(initialHardTopItemId);

	// states.
	const [selectedHoodItemId, setSelectedHoodItemId] = useState<string>(initialHoodItemId);
	const [selectedHoodItemName, setSelectedHoodItemName] = useState<string>(initialHoodItemName);
	const [selectedHardTopItemId, setSelectedHardTopItemId] = useState<string>(initialHardTopItemId);
	const [selectedHardTopItemName, setSelectedHardTopItemName] = useState<string>(initialHardTopItemName);

	// handlers.

	const handleHoodItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedHoodItemId(id);
		setSelectedHoodItemName(getItemName(id));

		// update the configuration.
		Configuration.hoodStyleOption = id;
		Configuration.updateCosts();

		// select the hood style on the car.
		props.exteriorConfigurator.selectHoodStyle(id);
	};

	const handleHardTopItemClicked = (id: string) => {

		// if the item is already selected, toggle it to "None" (no hard top).
		if (id === selectedHardTopItemId) {
			id = HardTopOptions.None;
		}

		// update the selected item and item name.
		setSelectedHardTopItemId(id);
		let hardTopName = getHardTopItemName(id);

		if (id === HardTopOptions.None) {
			hardTopName = '';
		}

		setSelectedHardTopItemName(hardTopName);

		// update the configuration.
		Configuration.hardTopOption = id as HardTopOptions;
		Configuration.updateCosts();

		// reset hood colour to make door panels black.
		if (id === HardTopOptions.BodyColoured) {
			handleHoodItemClicked(Configuration.defaultHoodOption);
		}

		// select the hard top style on the car.
		props.exteriorConfigurator.selectHardTop(id, context.hoodUp);

		// also set visibility of hard top icon in bottom left UI.
		context.setHardTopVisible(id !== HardTopOptions.None);
	};

	// return the HTML.
	return (
		<ExteriorTextilesViewContext.Provider value={{ selectedHoodItemId, handleHoodItemClicked, selectedHardTopItemId, handleHardTopItemClicked, toggleHood: context.toggleHood, hoodUp: context.hoodUp, hardTopVisible: context.hardTopVisible, toggleHardTop: context.toggleHardTop }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-exterior-textiles">
							ROOF
						</h2>
					</div>

					{selectedHardTopItemId !== HardTopOptions.BodyColoured && (
						<SectionPanel
							subHeading={'HOOD'}
							modalContent={CloudData.getModalContent(selectedHoodItemId, CloudData.hoodStyleOptions)}>
							<SelectedSectionItem
								itemName={selectedHoodItemName}
								sectionPrice={CloudData.getPrice(selectedHoodItemId, CloudData.hoodStyleOptions, { ref: false })}
							/>
							<HoodStyleItems />
						</SectionPanel>
					)}

					<SectionPanel
						subHeading={'HARD TOP'}
						modalContent={CloudData.getModalContent(selectedHardTopItemId, CloudData.hardTopOptions)}
					>
						<SelectedSectionItem
							itemName={selectedHardTopItemName}
							sectionPrice={CloudData.getPrice(selectedHardTopItemId, CloudData.hardTopOptions, { ref: false })}
						/>
						<HardTopItems />
					</SectionPanel>

					{selectedHardTopItemId !== HardTopOptions.BodyColoured && (
						<SectionPanel
							subHeading={'HOOD VIEW'}>
							<SelectedSectionItem
								itemName={'Click to view hood up/down'} />
							<HoodViewItems />
						</SectionPanel>
					)}

				</div>
			</VehicleView>
		</ExteriorTextilesViewContext.Provider>
	);
}

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const ExteriorTextilesViewContext = createContext<ExteriorTextilesViewContextType | undefined>(undefined);

function HoodStyleItems() {
	return (
		<ViewContentRows>
			<HoodStyleItem id={'H001'} imageFilePath={'black'} />
			<HoodStyleItem id={'H002'} imageFilePath={'green'} />
			<HoodStyleItem id={'H003'} imageFilePath={'blue'} />
			<HoodStyleItem id={'H004'} imageFilePath={'grey'} />
			<HoodStyleItem id={'H005'} imageFilePath={'beige'} />
			<HoodStyleItem id={'H006'} imageFilePath={'maroon'} />
			<HoodStyleItem id={'H007'} imageFilePath={'brown'} />
		</ViewContentRows>
	);
};

function HoodStyleItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(ExteriorTextilesViewContext);
	if (!context) {
		throw new Error('ExteriorTextilesViewContext must be used within a ExteriorTextilesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/hood/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedHoodItemId}
			onItemClicked={context.handleHoodItemClicked!}
		/>
	);
}

function HardTopItems() {
	return (
		<ViewContentRows>
			<HardTopItem id={'HT003'} imageFilePath={'std-hard-top-body'} />
			{!Configuration.isSupersportModelInUsRegion() && <HardTopItem id={'HT004'} imageFilePath={'std-hard-top-mohair'} />}
		</ViewContentRows>
	);
};

function HardTopItems400() {
	return (
		<ViewContentRows>
			<HardTopItem id={'HT003'} imageFilePath={'hard-top-body'} />
			<HardTopItem id={'HT004'} imageFilePath={'hard-top-mohair'} />
			<HardTopItem id={'HT009'} imageFilePath={'hard-top-grey'} />
			<HardTopItem id={'HT010'} imageFilePath={'hard-top-grey-mohair'} />
		</ViewContentRows>
	);
};

function HardTopItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(ExteriorTextilesViewContext);
	if (!context) {
		throw new Error('ExteriorTextilesViewContext must be used within a ExteriorTextilesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getHardTopItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/hard-top/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedHardTopItemId}
			onItemClicked={context.handleHardTopItemClicked}
		/>
	);
}

function HoodViewItems() {

	// get the context and ensure it is valid.
	const context = useContext(ExteriorTextilesViewContext);
	if (!context) {
		throw new Error('ExteriorTextilesViewContext must be used within a ExteriorTextilesView component');
	}

	// return the HTML.
	return (
		<ViewContentRows>
			<SectionItem
				id={'hood-toggle'}
				imageFilePath={'assets/images/icons/inline-hood-toggle.svg'}
				isSelected={context.hoodUp && !context.hardTopVisible}
				onItemClicked={context.toggleHood}

				// we actually hide the whole section if either hardtop opion is selected, but good to keep the rules here in case we change this.
				isDisabled={Configuration.isHardTopOnly(context.selectedHardTopItemId as HardTopOptions)}
				itemName=""
			/>
			<SectionItem
				id={'hard-top-toggle'}
				imageFilePath={'assets/images/icons/inline-hard-top-toggle.svg'}
				isSelected={context.hardTopVisible}
				onItemClicked={context.toggleHardTop}
				isDisabled={!Configuration.isHardTopAndSoftTop(context.selectedHardTopItemId as HardTopOptions)}
				itemName=""
			/>
		</ViewContentRows>
	);
};

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.hoodStyleOptions);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getHardTopItemName(id: string): string {
	return CloudData.getName(id, CloudData.hardTopOptions);
}
